import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
  faShieldAlt,
  faUsers,
  faNewspaper,
  faBullhorn,
  faCreditCard,
  faWallet,
  faLock,
  faExclamationTriangle,
  faChild,
  faTrash,
  faPhone,
  faEnvelope,
  faGlobe,
  faCheckCircle,
  faStar,
  faHeart,
  faRocket
} from '@fortawesome/free-solid-svg-icons';

const Faqs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const faqs = [
    {
      id: 1,
      question: "What is IINSAF?",
      icon: faQuestionCircle,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      answer: [
        "IINSAF is a digital platform that connects Reporters, Advertisers, and Readers.",
        "Reporters can earn by publishing verified news and advertisements.",
        "Advertisers can promote their products and services.",
        "Readers can access authentic news and updates."
      ]
    },
    {
      id: 2,
      question: "How can I become a Reporter on IINSAF?",
      icon: faUsers,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      borderColor: "border-green-200",
      answer: [
        "Register on the website with your Name, Phone, Email, and ID proof (Aadhaar/PAN).",
        "Once verified, you can start receiving assignments and advertisements."
      ]
    },
    {
      id: 3,
      question: "How can Advertisers publish their ads?",
      icon: faBullhorn,
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      answer: [
        "Advertisers can create an account and submit their ad content.",
        "All ads go through a verification process before being published.",
        "Payment must be made via Razorpay Payment Gateway."
      ]
    },
    {
      id: 4,
      question: "What payment methods are accepted?",
      icon: faCreditCard,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      borderColor: "border-orange-200",
      answer: [
        "We accept payments through Razorpay, which supports UPI, credit/debit cards, and net banking.",
        "Payouts to reporters are done through UPI ID or Bank Account."
      ]
    },
    {
      id: 5,
      question: "Do you offer refunds?",
      icon: faWallet,
      color: "from-indigo-500 to-indigo-600",
      bgColor: "from-indigo-50 to-indigo-100",
      borderColor: "border-indigo-200",
      answer: [
        "No direct cash refunds are provided.",
        "Any eligible refund is credited to your IINSAF Wallet.",
        "Wallet balance can be reused for future services on the platform but cannot be withdrawn."
      ]
    },
    {
      id: 6,
      question: "Is my data safe on IINSAF?",
      icon: faShieldAlt,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "from-emerald-50 to-emerald-100",
      borderColor: "border-emerald-200",
      answer: [
        "Yes ✅.",
        "Your personal details (name, phone, email, Aadhaar/PAN, bank details) are kept secure and confidential.",
        "We never sell or misuse user data."
      ]
    },
    {
      id: 7,
      question: "What content is prohibited on IINSAF?",
      icon: faExclamationTriangle,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      borderColor: "border-red-200",
      answer: [
        "Nudity or adult content",
        "Fraudulent or misleading ads",
        "Illegal online gaming or gambling",
        "Hate speech, scams, or plagiarism",
        "Violators will face account suspension and legal action."
      ]
    },
    {
      id: 8,
      question: "Can children use IINSAF?",
      icon: faChild,
      color: "from-pink-500 to-pink-600",
      bgColor: "from-pink-50 to-pink-100",
      borderColor: "border-pink-200",
      answer: [
        "Yes, the platform is safe for children to read news.",
        "However, only users above 18 years can register as reporters or advertisers."
      ]
    },
    {
      id: 9,
      question: "Can I delete my account and data?",
      icon: faTrash,
      color: "from-gray-500 to-gray-600",
      bgColor: "from-gray-50 to-gray-100",
      borderColor: "border-gray-200",
      answer: [
        "Yes. You can request to delete or update your personal data anytime by contacting our support team."
      ]
    },
    {
      id: 10,
      question: "How can I contact IINSAF for support?",
      icon: faPhone,
      color: "from-cyan-500 to-cyan-600",
      bgColor: "from-cyan-50 to-cyan-100",
      borderColor: "border-cyan-200",
      answer: [
        "Phone: 9992396623",
        "Email: iinsafofficial@gmail.com",
        "Website: www.iinsaf.com"
      ]
    }
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-12"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12 md:p-16 lg:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              FREQUENTLY ASKED QUESTIONS
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">
                FAQs
              </span>{' '}
              – IINSAF
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Find answers to the most common questions about our platform, services, and policies. 
              Can't find what you're looking for?{' '}
              <span className="font-semibold text-blue-600">Contact us</span> for personalized assistance.
            </p>
          </motion.div>
          </div>
      </motion.div>

      {/* FAQs Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              variants={staggerItem}
              className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className={`w-full p-6 sm:p-8 text-left bg-gradient-to-r ${faq.bgColor} border-b ${faq.borderColor} hover:shadow-md transition-all duration-300`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 bg-gradient-to-r ${faq.color} rounded-xl flex items-center justify-center mr-4 shadow-lg`}>
                      <FontAwesomeIcon icon={faq.icon} className="text-white text-xl" />
          </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                      {faq.id}. {faq.question}
                    </h3>
          </div>
                  <motion.div
                    animate={{ rotate: openFAQ === faq.id ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0 ml-4"
                  >
                    <FontAwesomeIcon 
                      icon={openFAQ === faq.id ? faChevronUp : faChevronDown} 
                      className="text-gray-600 text-xl" 
                    />
                  </motion.div>
          </div>
              </button>
              
              <AnimatePresence>
                {openFAQ === faq.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 sm:p-8 bg-white/50">
                      <div className="space-y-3">
                        {faq.answer.map((item, itemIndex) => (
                          <motion.div
                            key={itemIndex}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                            className="flex items-start"
                          >
                            <FontAwesomeIcon 
                              icon={faCheckCircle} 
                              className={`text-${faq.color.split('-')[1]}-500 mt-1 mr-3 flex-shrink-0`} 
                            />
                            <span className="text-gray-700 leading-relaxed">
                              {item.includes('Violators will face') ? (
                                <span className="text-red-600 font-semibold">{item}</span>
                              ) : (
                                item
                              )}
                            </span>
                          </motion.div>
                        ))}
          </div>
          </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white"
        >
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
          </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Still Have Questions?</h2>
          </div>
          <p className="text-white/90 mb-6 text-lg">
            Can't find the answer you're looking for? Our support team is here to help you 24/7.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faPhone} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:9992396623" className="text-white/90 hover:text-white transition-colors duration-300">
                  9992396623
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:iinsafofficial@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300">
                  iinsafofficial@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faGlobe} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Website</p>
                <a href="https://www.iinsaf.com" className="text-white/90 hover:text-white transition-colors duration-300" target="_blank" rel="noopener noreferrer">
                  www.iinsaf.com
                </a>
              </div>
          </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Faqs;